/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, December 28th 2021, 10:22:42 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { useRef, useEffect, useCallback, DependencyList, EffectCallback, useMemo } from "react";
import { isEqual, cloneDeep } from "lodash";

const useDeepComparison = (dependencies: DependencyList): DependencyList => {
  const previousDependencies = useRef<DependencyList>([]);

  const dependenciesHaveChanged = !isEqual(previousDependencies.current, dependencies);

  const triggerChangeRef = useRef<boolean>(false);

  const handleDependencyChange = () => {
    previousDependencies.current = cloneDeep(dependencies);
    triggerChangeRef.current = !triggerChangeRef.current;
  };

  dependenciesHaveChanged && handleDependencyChange();

  return [triggerChangeRef.current];
};

/** Only use this if absolutely necessary with no potential side effects on performance and dependencies do not include non serializable values such as functions. */
export const useDeepEffect = (effectCallback: EffectCallback, dependencies?: DependencyList) => {
  //eslint-disable-next-line react-hooks/exhaustive-deps
  return useEffect(effectCallback, useDeepComparison(dependencies));
};

/** Only use this if absolutely necessary with no potential side effects on performance and dependencies do not include non serializable values such as functions. */
export const useDeepCallback = <T extends (...args: any[]) => any>(callback: T, dependencies?: DependencyList): T => {
  //eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(callback, useDeepComparison(dependencies));
};

/** Only use this if absolutely necessary with no potential side effects on performance and dependencies do not include non serializable values such as functions. */
export const useDeepMemo = <T>(factory: () => T, dependencies?: DependencyList): T => {
  //eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo<T>(factory, useDeepComparison(dependencies));
};
